import {
  MANDATORY_SESSION_EXTENSION_COUNT_REDUX_CEILING,
  MANDATORY_SESSION_EXTENSION_SLOT_MINUTES,
  MANDATORY_SESSION_MAX_EXTENSIONS,
} from '@ecrm-session'

declare global {
  interface Window {
    _env_: {
      VITE_ENVIRONMENT: string
      VITE_LOGIN_URL_DOMAIN_DEV: string
      VITE_LOGIN_URL_CLIENT_ID: string
      VITE_AUTH_PROVIDER: string
      VITE_API_URL: string
      VITE_MSAL_CLIENT_ID: string
      VITE_MSAL_AUTHORITY_URL: string
      VITE_WEBSOCKET_URLS: string
      VITE_AWS_INSTANCE_URL: string
      VITE_MSAL_SCOPE: string
      VITE_ENABLE_AWS_CONNECT: string
      VITE_PENDO_ENABLE: boolean
      VITE_PENDO_KEY: string
      VITE_AWS_LOGIN_URL: string
      VITE_SESSION_INACTIVITY_TIMEOUT_MINUTES?: string
      VITE_SESSION_INACTIVITY_WARNING_MINUTES?: string
      /** Max session wall time in minutes before mandatory re-login (0 = disabled). */
      VITE_MANDATORY_RELOGIN_AFTER_MINUTES?: string
      /** Minutes before max session end to show the advisory modal (default 2). */
      VITE_MANDATORY_RELOGIN_WARNING_MINUTES?: string
      VITE_DD_CLIENT_TOKEN_AGENT?: string
      VITE_DD_APPLICATION_ID_AGENT?: string
      /** Minutes each “Extend” adds to max session (default 60). */
      VITE_MANDATORY_SESSION_EXTENSION_SLOT_MINUTES?: string
      /** Max number of session extensions (default 2). */
      VITE_MANDATORY_SESSION_MAX_EXTENSIONS?: string
    }
  }
}

function parseEnvPositiveInt(
  value: string | undefined,
  fallback: number
): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback
}

/** Non-negative minutes; fractional values allowed (e.g. 0.5). 0 = disabled. */
function parseEnvNonNegativeMinutes(
  value: string | undefined,
  fallback: number
): number {
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

export const environment = {
  environment:
    window._env_.VITE_ENVIRONMENT || import.meta.env.VITE_ENVIRONMENT,
  loginUrlDomainDev:
    window._env_.VITE_LOGIN_URL_DOMAIN_DEV ||
    import.meta.env.VITE_LOGIN_URL_DOMAIN_DEV,
  loginUrlClientId:
    window._env_.VITE_LOGIN_URL_CLIENT_ID ||
    import.meta.env.VITE_LOGIN_URL_CLIENT_ID,
  authProvider:
    window._env_.VITE_AUTH_PROVIDER || import.meta.env.VITE_AUTH_PROVIDER,
  apiUrl: window._env_.VITE_API_URL || import.meta.env.VITE_API_URL,
  msalConfig: {
    clientId:
      window._env_.VITE_MSAL_CLIENT_ID || import.meta.env.VITE_MSAL_CLIENT_ID,
    authorityUrl:
      window._env_.VITE_MSAL_AUTHORITY_URL ||
      import.meta.env.VITE_MSAL_AUTHORITY_URL,
    msalScope: window._env_.VITE_MSAL_SCOPE || import.meta.env.VITE_MSAL_SCOPE,
  },
  websocketUrls:
    window._env_.VITE_WEBSOCKET_URLS || import.meta.env.VITE_WEBSOCKET_URLS,
  awsConnectUrl:
    window._env_.VITE_AWS_INSTANCE_URL || import.meta.env.VITE_AWS_INSTANCE_URL,
  enableCall:
    window._env_.VITE_ENABLE_AWS_CONNECT ||
    import.meta.env.VITE_ENABLE_AWS_CONNECT,
  pendoEnable:
    window._env_.VITE_PENDO_ENABLE || import.meta.env.VITE_PENDO_ENABLE,
  pendoKey: window._env_.VITE_PENDO_KEY || import.meta.env.VITE_PENDO_KEY,
  awsLoginUrl:
    window._env_.VITE_AWS_LOGIN_URL || import.meta.env.VITE_AWS_LOGIN_URL,
  /** Inactivity timeout in minutes (0 = feature disabled). */
  sessionInactivityTimeoutMinutes: parseEnvPositiveInt(
    window._env_.VITE_SESSION_INACTIVITY_TIMEOUT_MINUTES ??
      import.meta.env.VITE_SESSION_INACTIVITY_TIMEOUT_MINUTES,
    0
  ),
  /**
   * How many minutes before hard timeout to show the blocking warning modal.
   * Must be less than sessionInactivityTimeoutMinutes when both are set.
   */
  sessionInactivityWarningMinutes: parseEnvPositiveInt(
    window._env_.VITE_SESSION_INACTIVITY_WARNING_MINUTES ??
      import.meta.env.VITE_SESSION_INACTIVITY_WARNING_MINUTES,
    0
  ),
  /** 0 = mandatory re-login after max session duration is disabled. */
  mandatoryReloginAfterMinutes: parseEnvNonNegativeMinutes(
    window._env_.VITE_MANDATORY_RELOGIN_AFTER_MINUTES ??
      import.meta.env.VITE_MANDATORY_RELOGIN_AFTER_MINUTES,
    0
  ),
  /** Minutes before max session end to show the advisory modal. */
  mandatoryReloginWarningMinutes: parseEnvPositiveInt(
    window._env_.VITE_MANDATORY_RELOGIN_WARNING_MINUTES ??
      import.meta.env.VITE_MANDATORY_RELOGIN_WARNING_MINUTES,
    2
  ),
  ddClientTokenAgent:
    window._env_.VITE_DD_CLIENT_TOKEN_AGENT ||
    import.meta.env.VITE_DD_CLIENT_TOKEN_AGENT,
  ddApplicationIdAgent:
    window._env_.VITE_DD_APPLICATION_ID_AGENT ||
    import.meta.env.VITE_DD_APPLICATION_ID_AGENT,
  /** Minutes per “Extend” click (multiplied into ms in session UI). */
  mandatorySessionExtensionSlotMinutes: parseEnvPositiveInt(
    window._env_.VITE_MANDATORY_SESSION_EXTENSION_SLOT_MINUTES ??
      import.meta.env.VITE_MANDATORY_SESSION_EXTENSION_SLOT_MINUTES,
    MANDATORY_SESSION_EXTENSION_SLOT_MINUTES
  ),
  /** Max extensions after the base max window (capped for Redux guardrail). */
  mandatorySessionMaxExtensions: Math.min(
    MANDATORY_SESSION_EXTENSION_COUNT_REDUX_CEILING,
    parseEnvPositiveInt(
      window._env_.VITE_MANDATORY_SESSION_MAX_EXTENSIONS ??
        import.meta.env.VITE_MANDATORY_SESSION_MAX_EXTENSIONS,
      MANDATORY_SESSION_MAX_EXTENSIONS
    )
  ),
} as const
